import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Lock, ShieldAlert, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const STORAGE_KEY = "wibiz_internal_unlocked";

interface WibizInternalLockProps {
  children: React.ReactNode;
}

export default function WibizInternalLock({ children }: WibizInternalLockProps) {
  const [unlocked, setUnlocked] = useState(() => {
    return sessionStorage.getItem(STORAGE_KEY) === "true";
  });
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const verifyMutation = trpc.wibizInternalAuth.verify.useMutation({
    onSuccess: () => {
      sessionStorage.setItem(STORAGE_KEY, "true");
      setUnlocked(true);
    },
    onError: () => {
      toast.error("Incorrect password.");
      setPassword("");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) return;
    verifyMutation.mutate({ password });
  };

  if (unlocked) return <>{children}</>;

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-slate-900 border border-red-900/40 shadow-lg mb-4">
            <ShieldAlert className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Wibiz Internal</h1>
          <p className="text-slate-500 text-sm mt-1">Not for BizConnect360 staff</p>
        </div>

        <Card className="border-slate-800 bg-slate-900/80 backdrop-blur shadow-2xl">
          <CardHeader className="text-center pb-4">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Lock className="w-4 h-4 text-red-500" />
              <CardTitle className="text-white text-lg">Restricted — Wibiz Staff Only</CardTitle>
            </div>
            <CardDescription className="text-slate-500">
              This page shows Wibiz's own cost and margin data. It uses a separate
              credential from the BizConnect360 admin and voice-center passwords.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="wibiz-password" className="text-slate-400 text-sm">
                  Wibiz Internal Password
                </Label>
                <div className="relative">
                  <Input
                    id="wibiz-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-600 pr-10 focus:border-red-600 focus:ring-red-600/20"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-red-700 hover:bg-red-600 text-white font-semibold shadow-lg"
                disabled={verifyMutation.isPending || !password.trim()}
              >
                {verifyMutation.isPending ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Verifying...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    Unlock
                  </span>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
